/**
 * Resolve a room into its boundary polygon via polygon union.
 *
 * The base rectangle (with corner chamfers) and each outward alcove (with its
 * chamfers) are built as independent rings, then unioned. Shared interior
 * walls cancel automatically, so two touching alcoves merge and the wall
 * between them disappears, exactly as intended. Every wall runs floor to
 * ceiling, so each boundary segment is a wall as high as the room.
 *
 * The model-geometry helpers this pipeline builds on (Point, edge/line frames,
 * the canonical alcove ring, rectRing, ring math) live alongside it in
 * ./geometry/.
 */

import polygonClipping, { type MultiPolygon as PcMultiPolygon, type Ring as PcRing } from 'polygon-clipping';
import { alcoveRingVerts, edgeFrame, edgeLeg, ringSignedArea, samePoint, toPcRing, worldOnLine } from './geometry';
import { EDGE_NAMES, edgeSegments, FloorPlanRoom, Point, roomCeilingHeight } from './schemas';

export interface Segment {
  a: Point;
  b: Point;
  /** Wall height of this segment: the room's ceiling height. */
  height: number;
}

/** Resolved geometry: a multipolygon (rings) plus its boundary as wall segments. */
export interface ResolvedPerimeter {
  /** MultiPolygon: polygons → rings → points. Ring[0] is outer, rest holes. */
  polygons: Point[][][];
  /** The outer ring of the largest polygon, for convenience/rendering. */
  outline: Point[];
  /** Every boundary segment, as a wall up to the ceiling. */
  segments: Segment[];
}

/** A ring with each run of coincident vertices merged into its last one, and the closing repeat of the first dropped. */
function cleanRing(points: Point[]): Point[] {
  const clean: Point[] = [];
  for (const point of points) {
    if (clean.length > 0 && samePoint(clean[clean.length - 1], point)) clean[clean.length - 1] = point;
    else clean.push(point);
  }
  while (clean.length > 1 && samePoint(clean[clean.length - 1], clean[0])) clean.pop();
  return clean;
}

/** The chamfered base rectangle (no alcoves) as a ring: each edge's span between its corner chamfers. */
const baseRing = (shape: FloorPlanRoom): Point[] =>
  cleanRing(EDGE_NAMES.flatMap(edge => {
    const frame = edgeFrame(edge, shape.width, shape.length);
    return [worldOnLine(frame, edgeLeg(shape, edge, true), 0), worldOnLine(frame, frame.length - edgeLeg(shape, edge, false), 0)];
  }));

/** The room footprint: the base rectangle unioned with every alcove. */
export function roomFootprint(shape: FloorPlanRoom): Point[][][] {
  const alcoves: PcRing[][] = EDGE_NAMES.flatMap(edge =>
    edgeSegments(shape, edge)
      .filter(segment => segment.edgeSegmentType === 'alcove')
      .map(alcove => [toPcRing(cleanRing(alcoveRingVerts(shape, edge, alcove)))])
  );
  const base: PcRing[] = [toPcRing(baseRing(shape))];
  const footprint: PcMultiPolygon = alcoves.length === 0 ? polygonClipping.union(base) : polygonClipping.union(base, ...alcoves);
  return pcToPolygons(footprint);
}

/** Every edge of every ring of `polygons`, as a wall `height` high. */
export const boundarySegments = (polygons: Point[][][], height: number): Segment[] =>
  polygons.flatMap(polygon => polygon.flatMap(ring => ring.map((a, index) => ({ a, b: ring[(index + 1) % ring.length], height }))));

export function resolvePerimeter(shape: FloorPlanRoom): ResolvedPerimeter {
  const polygons = roomFootprint(shape);
  return { polygons, outline: largestOuterRing(polygons), segments: boundarySegments(polygons, roomCeilingHeight(shape)) };
}

export function pcToPolygons(mp: PcMultiPolygon): Point[][][] {
  return mp.map(poly => poly.map(ring => stripClose(ring).map(([x, y]) => ({ x, y }))));
}

export function toPcMultiPolygon(polygons: Point[][][]): PcMultiPolygon {
  return polygons.map(poly => poly.map(ring => toPcRing(ring)));
}

function stripClose(ring: PcRing): PcRing {
  if (ring.length > 1) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) return ring.slice(0, -1);
  }
  return ring;
}

function largestOuterRing(polygons: Point[][][]): Point[] {
  let best: Point[] = [];
  let bestArea = -1;
  for (const poly of polygons) {
    if (poly.length === 0) continue;
    const ring = poly[0];
    const area = Math.abs(ringSignedArea(ring));
    if (area > bestArea) {
      bestArea = area;
      best = ring;
    }
  }
  return best;
}
